import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type {
  ITileManagerModel,
  IConnectionModel,
  IChipInstanceModel,
} from '@/interfaces/models';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { TileType } from '@/models/TileTypes';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { canSeparateDecoration } from './DecorativeTopologyPolicy';
import { isNativeConnectionSelectorSource } from '@/models/connections/ConnectionModel';
import type { TileId } from '@/types/ids';
import {
  MIN_TILE_POSITION_VALUE,
  MAX_TILE_POSITION_VALUE,
  hasNativeTileLookups,
  isConstructedTileCollection,
} from '@/models/TileManagerModel';

export interface TopologyEntry {
  signature: string;
  authoredSignature?: string;
  connectionLinks?: string[];
  tileType?: TileType;
  owner: object;
  neighbors: Set<string>;
  nodeIds: Set<NodeHandle>;
  decorative?: boolean;
  decorationEdges?: Set<string>;
  nativeDecoration?: boolean;
  ownerChip?: string | null;
}
export type TopologySnapshot = Map<string, TopologyEntry>;
export const chipVertex = (id: string) => `chip:${id}`;
const topologyManagers = new WeakMap<TopologySnapshot, ITileManagerModel>();
const topologyConnections = new WeakMap<TopologySnapshot, IConnectionModel | undefined>();
const topologyTypes = new WeakMap<TopologySnapshot, Set<TileType>>();
const wirelessSnapshots = new WeakSet<TopologySnapshot>();
const bundleSnapshots = new WeakSet<TopologySnapshot>();
const nativeSnapshots = new WeakSet<TopologySnapshot>();
export const hasNativeTopologyBuilders = (snapshot: TopologySnapshot) =>
  nativeSnapshots.has(snapshot);
const patchedSnapshots = new WeakSet<TopologySnapshot>();
export const isPatchedTopology = (snapshot: TopologySnapshot) =>
  patchedSnapshots.has(snapshot);

/** Complete authored edit delta. Callers must include both endpoints of changed
 * connections and every changed chip definition/instance. Ordinary explicit
 * capture/rebuild remains fresh and does not infer a patch from cached data. */
export interface GraphEditPatch {
  tileIds: ReadonlySet<string>;
  chipIds?: ReadonlySet<string>;
}

const isDecoration = (tile: TileInstance) =>
  tile.tileType === TileType.Structure &&
  !tile.logicNodeIds &&
  !tile.laneNodeIds &&
  !tile.effectiveIoSides;

const runtimeKeys: Partial<Record<TileType, string[]>> = {
  [TileType.Lever]: ['toggleState'],
  [TileType.Button]: ['toggleState'],
  [TileType.SingleInterface]: ['toggleState'],
  [TileType.Clock]: ['state', 'interval'],
  [TileType.Delay]: ['queue', 'lastInput', 'currentOutput'],
  [TileType.Random]: ['prevInput', 'lastOutput'],
};

function configuration(tile: TileInstance) {
  if (tile.tileType.startsWith('extension:'))
    return (
      tileDefinitionRegistry.get(tile.tileType).graphConfiguration?.(tile.state) ?? null
    );
  const state = { ...((tile.state as object) ?? {}) } as Record<string, unknown>;
  for (const key of runtimeKeys[tile.tileType] ?? []) delete state[key];
  return state;
}

function positionKey(x: number, y: number): number | string {
  // Native coordinates fit exactly in a JavaScript integer. Keep string keys
  // for custom/out-of-range positions rather than wrapping either axis.
  return Number.isInteger(x) &&
    Number.isInteger(y) &&
    x >= MIN_TILE_POSITION_VALUE &&
    x <= MAX_TILE_POSITION_VALUE &&
    y >= MIN_TILE_POSITION_VALUE &&
    y <= MAX_TILE_POSITION_VALUE
    ? (x - MIN_TILE_POSITION_VALUE) *
        (MAX_TILE_POSITION_VALUE - MIN_TILE_POSITION_VALUE + 1) +
        y -
        MIN_TILE_POSITION_VALUE
    : `${x},${y}`;
}

function tileNodeIds(tile: TileInstance): Set<NodeHandle> {
  const scalar = tile.logicNodeIds;
  const result = new Set(scalar ? Object.values(scalar) : []);
  const lanes = tile.laneNodeIds;
  if (lanes) for (const id of Object.values(lanes).flat()) result.add(id);
  return result;
}

export function captureTopology(
  manager: ITileManagerModel,
  connections: IConnectionModel | undefined,
  chips: IChipInstanceModel[],
  certifyDecoration = false,
  spatialManager?: ITileManagerModel,
): TopologySnapshot {
  const result: TopologySnapshot = new Map();
  topologyManagers.set(result, manager);
  topologyConnections.set(result, connections);
  // Initial builds may certify their history once. Edit snapshots defer the
  // expensive proof until the conservative affected component is known.
  const nativeDecoration =
    certifyDecoration && canSeparateDecoration(manager, undefined, connections);
  const wireless = new Map<string, string[]>();
  // Build one local position index. Four observable manager lookups per tile
  // dominated snapshot capture, including for isolated edits. Using this exact
  // tile list also respects the restricted manager used for partial builds.
  const tiles = manager.tiles;
  const types = new Set<TileType>();
  topologyTypes.set(result, types);
  const positions = new Map<number | string, TileInstance['id']>();
  const decorative = new Set<string>();
  for (const tile of tiles) {
    types.add(tile.tileType);
    if (tile.meta.bundle) bundleSnapshots.add(result);
    positions.set(positionKey(tile.location.x, tile.location.y), tile.id);
    if (isDecoration(tile)) decorative.add(tile.id);
  }
  for (const tile of tiles) {
    const membership =
      !tile.meta.partOfChip &&
      tileDefinitionRegistry.get(tile.tileType).wirelessChannel?.(tile.state);
    if (membership) {
      const key = JSON.stringify([tile.tileType.split('/')[0], membership.channel]);
      let group = wireless.get(key);
      if (!group) wireless.set(key, (group = []));
      group.push(tile.id);
    }
    const neighbors = connections?.connectedNeighbors?.(tile.id);
    const linked = neighbors
      ?.map((n) => `${n.viaSide}:${n.point.tileId}:${n.point.side}`)
      .sort();
    const { x, y } = tile.location;
    const adjacent = new Set(neighbors?.map((n) => n.point.tileId));
    let decorationEdges: Set<string> | undefined;
    const neighboringTiles = spatialManager?.getNeighbors(tile);
    const neighboringIds = neighboringTiles
      ? neighboringTiles.map((neighbor) => neighbor.id)
      : [
          positionKey(x - 1, y),
          positionKey(x + 1, y),
          positionKey(x, y - 1),
          positionKey(x, y + 1),
        ].map((position) => positions.get(position));
    const neighboringDecoration = neighboringTiles
      ? new Set(neighboringTiles.filter(isDecoration).map((neighbor) => neighbor.id))
      : decorative;
    for (const neighbor of neighboringIds) {
      if (neighbor) {
        if (
          !adjacent.has(neighbor) &&
          (decorative.has(tile.id) || neighboringDecoration.has(neighbor))
        )
          (decorationEdges ??= new Set()).add(neighbor);
        adjacent.add(neighbor);
      }
    }
    const authored = [
      tile.tileType,
      tile.location,
      tile.meta.rotation,
      tile.meta.partOfChip,
      tile.meta.bundle,
      tile.meta.cable,
      tile.effectiveIoSides,
      configuration(tile),
    ];
    result.set(tile.id, {
      tileType: tile.tileType,
      authoredSignature: JSON.stringify(authored),
      connectionLinks: linked,
      owner: tile,
      ownerChip: tile.meta.partOfChip,
      signature: JSON.stringify([...authored, linked]),
      neighbors: adjacent,
      nodeIds: tileNodeIds(tile),
      decorative: decorative.has(tile.id),
      decorationEdges,
      nativeDecoration,
    });
  }
  // A star joins the entire channel in O(P), including disconnected receivers.
  // Channel edits/deletions rebuild both old and new wireless components.
  for (const group of wireless.values())
    for (const id of group.slice(1)) result.get(group[0])!.neighbors.add(id);
  for (const chip of chips) {
    const key = chipVertex(chip.id);
    const entry: TopologyEntry = {
      owner: chip,
      signature: JSON.stringify([
        chip.chipId,
        chip.originPoint,
        chip.rotation,
        chip.bundleWidth,
        chip.chip?.templateGraphVersion,
      ]),
      neighbors: new Set(),
      nodeIds: new Set(chip.logicGraph.nodes.keys()),
    };
    result.set(key, entry);
  }
  for (const tile of tiles) {
    if (!tile.meta.partOfChip) continue;
    const key = chipVertex(tile.meta.partOfChip);
    result.get(key)?.neighbors.add(tile.id);
    result.get(tile.id)!.neighbors.add(key);
  }
  // One-sided explicit connections override geometric edge classification.
  for (const [id, entry] of result)
    for (const neighbor of entry.decorationEdges ?? []) {
      const reverse = result.get(neighbor);
      if (reverse?.neighbors.has(id) && !reverse.decorationEdges?.has(id))
        entry.decorationEdges!.delete(neighbor);
    }
  if (tileDefinitionRegistry.hasNativeGraphBuilders?.(types)) nativeSnapshots.add(result);
  // Include both directions even for a provider returning one-sided links.
  for (const [id, entry] of result)
    for (const neighbor of entry.neighbors) result.get(neighbor)?.neighbors.add(id);
  if (
    tileDefinitionRegistry.hasNativeGraphBuilders?.(types) &&
    [...types].some((type) => tileDefinitionRegistry.get(type).wirelessChannel)
  )
    wirelessSnapshots.add(result);
  return result;
}

/** Apply an explicit edit without rediscovering unchanged tile configuration.
 * The one-cell/connection halo refreshes both sides of joins and deletions.
 * Chip membership changes refresh that chip's vertex and member footprint.
 * Nonlocal bundle normalization, wireless/custom builders retain full capture. */
export function captureTopologyPatch(
  previous: TopologySnapshot,
  manager: ITileManagerModel,
  connections: IConnectionModel | undefined,
  chips: IChipInstanceModel[],
  patch: GraphEditPatch,
): TopologySnapshot {
  const types = new Set(topologyTypes.get(previous));
  let hasBundles = bundleSnapshots.has(previous);
  const getTile = (id: string) => manager.getById?.(id as TileId);
  for (const id of patch.tileIds) {
    const tile = getTile(id);
    if (tile) {
      types.add(tile.tileType);
      hasBundles ||= !!tile.meta.bundle;
    }
  }
  if (
    !manager.getById ||
    !hasNativeTileLookups(manager) ||
    !isConstructedTileCollection(manager, manager.tiles) ||
    (connections && !isNativeConnectionSelectorSource(connections)) ||
    !topologyTypes.has(previous) ||
    hasBundles ||
    wirelessSnapshots.has(previous) ||
    !tileDefinitionRegistry.hasNativeGraphBuilders?.(types) ||
    [...types].some((type) => tileDefinitionRegistry.get(type).wirelessChannel)
  )
    return captureTopology(manager, connections, chips);

  const tileIds = new Set(patch.tileIds);
  const chipIds = new Set(patch.chipIds);
  for (const id of patch.tileIds) {
    const old = previous.get(id);
    const tile = getTile(id);
    const owner = tile?.meta.partOfChip;
    if (old?.ownerChip !== owner) {
      if (old?.ownerChip) chipIds.add(old.ownerChip);
      if (owner) chipIds.add(owner);
    }
  }
  // A changed chip template may change every member, even if no cell moved.
  for (const id of chipIds)
    for (const member of previous.get(chipVertex(id))?.neighbors ?? [])
      if (!member.startsWith('chip:')) tileIds.add(member);
  for (const id of [...tileIds]) {
    for (const neighbor of previous.get(id)?.neighbors ?? [])
      if (!neighbor.startsWith('chip:')) tileIds.add(neighbor);
    const tile = getTile(id);
    if (!tile) continue;
    for (const neighbor of manager.getNeighbors(tile)) tileIds.add(neighbor.id);
    for (const neighbor of connections?.connectedNeighbors(tile.id) ?? [])
      tileIds.add(neighbor.point.tileId);
  }

  const tiles = [...tileIds].flatMap((id) => {
    const tile = getTile(id);
    return tile ? [tile] : [];
  });
  const subset = new Proxy(manager, {
    get(target, key) {
      return key === 'tiles' ? tiles : Reflect.get(target, key, target);
    },
  });
  const captured = captureTopology(
    subset,
    connections,
    chips.filter((chip) => chipIds.has(chip.id)),
    false,
    manager,
  );
  const result = new Map(previous);
  for (const id of [...tileIds, ...[...chipIds].map(chipVertex)]) {
    const entry = captured.get(id);
    if (entry) result.set(id, entry);
    else result.delete(id);
  }
  topologyManagers.set(result, manager);
  topologyConnections.set(result, connections);
  topologyTypes.set(result, types);
  patchedSnapshots.add(result);
  if (nativeSnapshots.has(previous)) nativeSnapshots.add(result);
  return result;
}

/** Union of old and new components covers both joins and splits/deletions. */
export function changedTopology(
  previous: TopologySnapshot,
  next: TopologySnapshot,
): Set<string> {
  const affected = new Set<string>();
  for (const [id, entry] of previous) {
    const replacement = next.get(id);
    if (
      !replacement ||
      replacement.owner !== entry.owner ||
      replacement.signature !== entry.signature ||
      replacement.decorative !== entry.decorative ||
      replacement.nodeIds.size !== entry.nodeIds.size ||
      [...entry.nodeIds].some((node) => !replacement.nodeIds.has(node))
    )
      affected.add(id);
  }
  for (const id of next.keys()) if (!previous.has(id)) affected.add(id);
  const seeds = new Set(affected);
  const expand = (
    skip?: (snapshot: TopologySnapshot, id: string, neighbor: string) => boolean,
  ) => {
    const result = new Set(seeds);
    const queue = [...result];
    for (let index = 0; index < queue.length; index++) {
      const id = queue[index];
      for (const snapshot of [previous, next])
        for (const neighbor of snapshot.get(id)?.neighbors ?? []) {
          if (!result.has(neighbor) && !skip?.(snapshot, id, neighbor)) {
            result.add(neighbor);
            queue.push(neighbor);
          }
        }
    }
    return result;
  };
  const conservative = expand();
  const manager = topologyManagers.get(next);
  const tiles = [...conservative].flatMap((id) => {
    const owner = next.get(id)?.owner;
    return owner && !id.startsWith('chip:') ? [owner as TileInstance] : [];
  });
  // Preserve certification only for graph data which actually survives the edit.
  for (const [id, entry] of next)
    entry.nativeDecoration = previous.get(id)?.nativeDecoration === true;
  const hasBoundary = [...conservative].some((id) =>
    [previous, next].some((snapshot) => {
      const entry = snapshot.get(id);
      return [...(entry?.decorationEdges ?? [])].some((neighbor) => {
        // A shared chip vertex already requires these tiles to rebuild together.
        const chip = [...(entry?.neighbors ?? [])].find((value) =>
          value.startsWith('chip:'),
        );
        return (
          !chip || !snapshot.has(chip) || !snapshot.get(neighbor)?.neighbors.has(chip)
        );
      });
    }),
  );
  if (!hasBoundary) {
    for (const id of conservative) {
      const entry = next.get(id);
      if (entry) entry.nativeDecoration = false;
    }
    return conservative;
  }
  const native =
    !!manager && canSeparateDecoration(manager, tiles, topologyConnections.get(next));
  if (!native) {
    for (const id of conservative) {
      const entry = next.get(id);
      if (entry) entry.nativeDecoration = false;
    }
    return conservative;
  }
  const canSkip = (snapshot: TopologySnapshot, id: string, neighbor: string) => {
    if (
      !snapshot.get(id)?.decorationEdges?.has(neighbor) ||
      !(snapshot.get(id)?.decorative || snapshot.get(neighbor)?.decorative)
    )
      return false;
    // Current native builders cannot erase dependencies of an older custom
    // graph. Both historical endpoints must be certified before pruning.
    return [id, neighbor].every(
      (key) => !previous.has(key) || previous.get(key)!.nativeDecoration === true,
    );
  };
  const pruned = expand(canSkip);
  for (const id of pruned) {
    const entry = next.get(id);
    if (entry) entry.nativeDecoration = true;
  }
  return pruned;
}
